import React, { createContext, useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import OrderTable from './components/OrderTable';
import MapComponent from './components/MapComponent';
import OrderExtraInfo from './components/OrderExtraInfo';
import ClientSelector from './components/ClientSelector';
import AdminPortal from './components/AdminPortal';
import './styles/global.css';
import './components/App.css';
import './styles/responsive.css';
import { fetchOrders, fetchOrderDetails, getUserInfo } from './services/api';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import SignInButton from './components/SignInButton';
import SignOutButton from './components/SignOutButton';
import ShareModal from './components/ShareModal';
import Radar from './components/Radar';

/** Company mark. Left side of the signed-in header, and the login bar. */
const LOGIN_JTG = `${process.env.PUBLIC_URL || ''}/jtg-logo.png`;
/** LiveLink product mark. Login screen, and the right side of the signed-in header. */
const LOGIN_LIVELINK = `${process.env.PUBLIC_URL || ''}/livelink-mark.png`;
const LOGIN_STAMP = `${process.env.PUBLIC_URL || ''}/top-fleet-stamp.png`;
const LOGIN_EMPLOYER = `${process.env.PUBLIC_URL || ''}/top-fleet-employer.png`;
const LOGIN_BEST_MANAGED = `${process.env.PUBLIC_URL || ''}/best-managed.png`;

/** Context for order selection, map coords, and ETA across LiveLink panels */
export const OrderContext = createContext({
  selectedOrder: null,
  setSelectedOrder: () => {},
  instance: null,
  coordinates: {
    pickup: null,
    delivery: null
  },
  setCoordinates: () => {},
  etaInfo: null,
  setEtaInfo: () => {},
  currentLocation: null,
  setCurrentLocation: () => {},
  /** Backend route_window phase + GPS points (single owner for portal map). */
  routeWindow: null,
  setRouteWindow: () => {},
  routeCoordinates: [],
  setRouteCoordinates: () => {}
});

/** Detects phone viewport (≤768); tablet/desktop stay side-by-side without tabs */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile;
}

/** LiveLink shell: auth, orders/map panels, admin route, share modal */
function App() {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userData, setUserData] = useState(null);

  const [accessDenied, setAccessDenied] = useState(false);

  const [shareOrder, setShareOrder] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());
  const [selectedClientId, setSelectedClientId] = useState(null);

  const [previewMode, setPreviewMode] = useState(false);
  const [coordinates, setCoordinates] = useState({ pickup: null, delivery: null });
  const [etaInfo, setEtaInfo] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [routeWindow, setRouteWindow] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);

  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  /** WebGL2 login streak. Reduced motion and missing WebGL skip it. */
  const [loginPillar] = useState(() => {
    try {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      return !reduce && !!document.createElement('canvas').getContext('webgl2');
    } catch {
      return false;
    }
  });
  const [loginHold, setLoginHold] = useState(loginPillar);
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState('orders');
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin';
  const headerMenuRef = useRef(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  useEffect(() => {
    setHeaderMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!headerMenuOpen) return undefined;
    const closeOnOutside = (event) => {
      if (!headerMenuRef.current?.contains(event.target)) setHeaderMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setHeaderMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [headerMenuOpen]);

  useEffect(() => {
    if (!loginPillar || isAuthenticated) return undefined;
    setLoginHold(true);
    const timer = setTimeout(() => setLoginHold(false), 2200);
    return () => clearTimeout(timer);
  }, [loginPillar, isAuthenticated]);

  useEffect(() => {
    const initializeApp = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const userInfo = await getUserInfo(instance);
        setUserData(userInfo);
        setAccessDenied(false);

        // Non-super-admins have a fixed client — fetch orders immediately.
        // Super Admins must pick a client first via the dropdown.
        if (!userInfo.is_super_admin) {
          const ordersData = await fetchOrders(selectedStartDate, selectedEndDate, instance);
          setOrders(ordersData);
        }
      } catch (err) {
        console.error('Error in initialization:', err);
        if (err.response && err.response.status === 403) {
          setAccessDenied(true);
        } else {
          setError('Failed to fetch data. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, [isAuthenticated, instance]);

  /** Reloads orders for the current date range and selected client */
  const handleSearch = async () => {
    setLoading(true);
    try {
      const ordersData = await fetchOrders(selectedStartDate, selectedEndDate, instance, selectedClientId);
      setOrders(ordersData);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to fetch orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /** Super Admin client switch: update logo and reload orders */
  const handleClientChange = async (clientId, clientLogo) => {
    setSelectedClientId(clientId);
    setUserData(prev => (prev ? { ...prev, logo_url: clientLogo } : prev));
    if (!clientId) {
      setOrders([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const ordersData = await fetchOrders(selectedStartDate, selectedEndDate, instance, clientId);
      setOrders(ordersData);
    } catch (err) {
      console.error('Error changing client:', err);
      setError('Failed to load data for selected client.');
    } finally {
      setLoading(false);
    }
  };

  /** Selects an order for the map panel; on phone switches to Tracking tab */
  const viewMap = (order) => {
    setSelectedOrder(order);
    setOrderDetails(null);
    setCoordinates({ pickup: null, delivery: null });
    setRouteWindow(null);
    setRouteCoordinates([]);
    setEtaInfo(null);
    setCurrentLocation(null);
    if (isMobile) setMobileTab('map');
  };

  /** Opens the order-details modal */
  const viewDetails = async (order) => {
    try {
      const details = await fetchOrderDetails(order.order_id, instance, selectedClientId);
      setOrderDetails(details);
    } catch {
      alert('Failed to load order details. Please try again.');
    }
  };

  /** Opens the share-link modal for an order */
  const viewShare = (order) => {
    setShareOrder(order);
    setShowShareModal(true);
  };

  /** Closes the order-details modal */
  const closeDetails = () => {
    setOrderDetails(null);
  };

  const contextValue = useMemo(
    () => ({
      orders,
      selectedOrder,
      orderDetails,
      viewMap,
      viewDetails,
      viewShare,
      closeDetails,
      selectedStartDate,
      setSelectedStartDate,
      selectedEndDate,
      setSelectedEndDate,
      selectedClientId,
      instance,
      coordinates,
      setCoordinates,
      etaInfo,
      setEtaInfo,
      currentLocation,
      setCurrentLocation,
      routeWindow,
      setRouteWindow,
      routeCoordinates,
      setRouteCoordinates
    }),
    [orders, selectedOrder, orderDetails, selectedStartDate, selectedEndDate, selectedClientId, instance, coordinates, etaInfo, currentLocation, routeWindow, routeCoordinates]
  );

  if (!isAuthenticated) {
    return (
      <div className={`login-container${loginHold ? ' login-hold' : ''}`}>
        {loginPillar && (
          <Radar
            className="login-radar"
            color="#ff0000"
            trail={0.34}
            intro
          />
        )}
        <header className="login-brand-bar">
          <img className="login-jtg" src={LOGIN_JTG} alt="Joseph" />
          <span className="login-group">Transportation<br />Group</span>
        </header>
        <div className="login-main">
          <img className="login-livelink" src={LOGIN_LIVELINK} alt="LiveLink" />
          <h2>Stay up to date.<br />Track your freight.</h2>
          <p className="login-copy">Live truck locations, ETAs, and load documents—all in one place.</p>
          <SignInButton />
          <p className="login-account">Use your work account</p>
        </div>
        <footer className="login-awards">
          <p>Recognized by</p>
          <div className="login-award-row">
            <img src={LOGIN_STAMP} alt="Top 100 Fleet 2026" />
            <img src={LOGIN_EMPLOYER} alt="Top Fleet Employer of distinction 2026" />
            <img src={LOGIN_BEST_MANAGED} alt="Canada's Best Managed Companies" />
          </div>
        </footer>
      </div>
    );
  }

  const appHeader = (
    <header className={`app-header${accessDenied ? '' : ' fade-in'}`}>
      <div className="header-left">
        <div className="header-logo">
          <img src={LOGIN_JTG} alt="Joseph Transportation Group" />
          {userData?.logo_url && (
            <>
              <span className="separator">
                <i className="fas fa-handshake"></i>
              </span>
              <img key={userData.logo_url} className="fade-in" src={userData.logo_url} alt="Partner Logo" />
            </>
          )}
        </div>
      </div>
      <div className="header-right">
        <div className="header-controls-desktop">
          {!accessDenied && userData?.is_super_admin && !previewMode && (
            isAdminPage ? (
              <Link to="/" className="admin-nav-link">Back to LiveLink</Link>
            ) : (
              <Link to="/admin" className="admin-nav-link">Admin</Link>
            )
          )}
          {!accessDenied && !isAdminPage && !previewMode && (
            <ClientSelector
              msalInstance={instance}
              onClientChange={handleClientChange}
              userData={userData}
              selectedClientId={selectedClientId}
            />
          )}
          {!accessDenied && userData?.is_super_admin && !isAdminPage && selectedClientId && (
            <button
              type="button"
              className={`preview-btn${previewMode ? ' preview-btn--active' : ''}`}
              onClick={() => setPreviewMode((prev) => !prev)}
            >
              {previewMode ? 'Exit Preview' : 'Preview as Client'}
            </button>
          )}
        </div>
        <div className="header-menu-wrap" ref={headerMenuRef}>
          <button
            type="button"
            className={`header-menu-btn${previewMode ? ' preview-btn--active' : ''}`}
            aria-expanded={headerMenuOpen}
            aria-controls="header-menu"
            aria-label="Account menu"
            onClick={() => setHeaderMenuOpen((open) => !open)}
          >
            <i className="fas fa-bars" aria-hidden="true"></i>
          </button>
          {headerMenuOpen && (
            <div className="header-menu" id="header-menu">
              <div className="header-controls-mobile">
                {!accessDenied && userData?.is_super_admin && !previewMode && (
                  isAdminPage ? (
                    <Link to="/" className="admin-nav-link">Back to LiveLink</Link>
                  ) : (
                    <Link to="/admin" className="admin-nav-link">Admin</Link>
                  )
                )}
                {!accessDenied && !isAdminPage && !previewMode && (
                  <ClientSelector
                    msalInstance={instance}
                    onClientChange={handleClientChange}
                    userData={userData}
                    selectedClientId={selectedClientId}
                    list
                  />
                )}
                {!accessDenied && userData?.is_super_admin && !isAdminPage && selectedClientId && (
                  <button
                    type="button"
                    className={`preview-btn${previewMode ? ' preview-btn--active' : ''}`}
                    onClick={() => setPreviewMode((prev) => !prev)}
                  >
                    {previewMode ? 'Exit Preview' : 'Preview as Client'}
                  </button>
                )}
              </div>
              <SignOutButton />
            </div>
          )}
        </div>
        <img className="header-product" src={LOGIN_LIVELINK} alt="LiveLink" />
      </div>
    </header>
  );

  if (accessDenied) {
    return (
      <div className="app-container">
        {appHeader}
        <div className="access-denied fade-up">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this application.</p>
        </div>
      </div>
    );
  }

  return (
    <OrderContext.Provider value={contextValue}>
      <div className="app-container">
        {appHeader}

        {isAdminPage && userData?.is_super_admin ? (
          <AdminPortal instance={instance} />
        ) : (
          <>
            {isMobile && (
              <div className="mobile-tabs">
                <button
                  type="button"
                  className={`tab-button ${mobileTab === 'orders' ? 'active' : ''}`}
                  onClick={() => setMobileTab('orders')}
                >
                  Orders
                </button>
                <button
                  type="button"
                  className={`tab-button ${mobileTab === 'map' ? 'active' : ''}`}
                  onClick={() => setMobileTab('map')}
                >
                  Tracking
                </button>
              </div>
            )}

            <main className="app-content">
              {(!isMobile || mobileTab === 'orders') && (
                <div className="left-panel fade-up" style={{ '--i': 1 }}>
                  {loading ? (
                    <div className="loading-spinner fade-in">Loading orders...</div>
                  ) : error ? (
                    <div className="error-message">{error}</div>
                  ) : (
                    <OrderTable
                      onDateChange={(startDate, endDate) => {
                        setSelectedStartDate(startDate);
                        setSelectedEndDate(endDate);
                      }}
                      onSearch={handleSearch}
                      instance={instance}
                    />
                  )}
                </div>
              )}
              {(!isMobile || mobileTab === 'map') && (
                <div className={`right-panel fade-up${isMobile ? ' right-panel--map-tab' : ''}`} style={{ '--i': 2 }}>
                  <MapComponent
                    currentLocationLabel={currentLocation}
                    fillParent={isMobile}
                  />
                  <OrderExtraInfo />
                </div>
              )}
            </main>
          </>
        )}

        {orderDetails && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Order Details: {orderDetails.order_id}</h2>
                <button type="button" className="close-button" onClick={closeDetails}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="detail-item">
                  <strong>Sender:</strong> {orderDetails.sender}
                </div>
                <div className="detail-item">
                  <strong>Receiver:</strong> {orderDetails.receiver}
                </div>
                <div className="detail-item">
                  <strong>Status:</strong> {orderDetails.status}
                </div>
                <div className="detail-item">
                  <strong>Delivery Date:</strong>{' '}
                  {orderDetails.delivery_date && !(new Date(orderDetails.delivery_date).getFullYear() === 1969)
                    ? orderDetails.delivery_date
                    : ''}
                </div>
              </div>
            </div>
          </div>
        )}
        {showShareModal && (
          <ShareModal
            order={shareOrder}
            onClose={() => {
              setShowShareModal(false);
              setShareOrder(null);
            }}
            instance={instance}
            selectedClientId={selectedClientId}
          />
        )}
      </div>
    </OrderContext.Provider>
  );
}

export default App;
